/**
 * DataContext — Global data refresh coordination
 *
 * Usage:
 *   // In any component after a mutation (create/update/delete):
 *   const triggerRefresh = useRefreshTrigger();
 *   await api.delete('/expenses/123');
 *   triggerRefresh(); // ← all subscribed pages refetch automatically
 *
 *   // In a page to subscribe to changes:
 *   const { refreshCounter } = useData();
 *   useEffect(() => { fetchData(); }, [refreshCounter]);
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

interface DataContextType {
  /** Increments every time any financial data is mutated */
  refreshCounter: number;
  /** Call this after any create / update / delete to trigger all pages to refetch */
  triggerRefresh: () => void;
}

const DataContext = createContext<DataContextType>({
  refreshCounter: 0,
  triggerRefresh: () => {},
});

export const DataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [refreshCounter, setRefreshCounter] = useState(0);

  const triggerRefresh = useCallback(() => {
    setRefreshCounter(c => c + 1);
  }, []);

  return (
    <DataContext.Provider value={{ refreshCounter, triggerRefresh }}>
      {children}
    </DataContext.Provider>
  );
};

/** Access the full context (counter + trigger) */
export const useData = () => useContext(DataContext);

/** Shorthand: just the trigger function */
export const useRefreshTrigger = () => useContext(DataContext).triggerRefresh;
